import { AnalyticsService } from 'base/analytics';
import { LibraryCache } from 'base/library_cache';
import { generateSchemes, getShadeKey } from 'base/styleguide/concept';
import { extractSectionMetadata } from 'base/styleguide/helpers';
import { CompiledScheme, parseShadeKey, ShadeKey } from 'base/styleguide/types';
import { Button } from 'base/ui/button';
import { ColorPip } from 'base/ui/ColorPip';
import { IconEdit, IconLogo, IconPng } from 'base/ui/icons';
import { Layer } from 'base/ui/Layer';
import { Inline, Rows } from 'base/ui/layout';
import { LottiePlayer } from 'base/ui/lottie';
import { globalResizeObserver } from 'base/ui/measure';
import { COLOR_TOKENS } from 'base/ui/tokens';
import { TextTinyLight } from 'base/ui/typography';
import { useStableCallback } from 'base/useStableCallback';
import { BlocksService } from 'blocks/blocks_service';
import { getBreakpointForViewport } from 'blocks/component_renderer/styles';
import { SectionRef } from 'blocks/editor/model';
import { unrefSection } from 'blocks/editor/model_helpers';
import { SelectionStore } from 'blocks/editor/selection/store';
import { SelectionActions } from 'blocks/editor/selection_actions';
import { ProjectSession } from 'blocks/editor/session/session';
import {
  ConceptAPI,
  getGlobalConceptsStore,
} from 'blocks/editor/styleguide/concept_store';
import {
  ColorsRoute,
  SidebarNavigation,
} from 'blocks/editor/styleguide/routes';
import {
  chromaticToNeutralShade,
  cyclePaletteInSequence,
  cycleShadeInSequence,
  neutralToChromaticShade,
} from 'blocks/editor/styleguide/style_application/helpers';
import {
  Breakpoint,
  BREAKPOINT_SIZE,
  BREAKPOINT_TO_CANVAS_SCALE,
} from 'blocks/editor/wireframe/breakpoint';
import { makeSectionVisibilityDecorators } from 'blocks/editor/wireframe/section';
import { Shortcut } from 'blocks/keyboard_shortcuts_dialog';
import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  CSSProperties,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { globalCardStyleOverrideStore } from './card_styles';
import clickAnimationLottie from './click_animation.lottie.json';
import { globalFontOverrideStore } from './fonts';
import styles from './homepage_preview.css';
import {
  findOverridableImagesInElement,
  populateImagesForSection,
} from './images';
import { globalConceptSectionHoverRegistry } from './image_styles';
import './scheme-override.global.css';
import {
  StyledElementsContext,
  StyledElementView,
  useLoadConceptFonts,
} from './styled_element_view';
import { globalTypeSettingsOverrideStore } from './type_settings';
import { globalUiStyleOverrideStore } from './ui_styles';

export const HomepagePreview = observer(function _HomepagePreview({
  analytics,
  navigation,
  session,
  selection,
  selectionActions,
  library,
  breakpoint,
  width = null,
  sectionLimit,
  concept,
  editing,
  blocksService,
  isCmdPressed = false,
}: {
  analytics: AnalyticsService;
  navigation?: SidebarNavigation;
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  library: LibraryCache;
  breakpoint: Breakpoint;
  width?: number | null;
  concept: ConceptAPI;
  sectionLimit?: number;
  editing?: boolean;
  blocksService: BlocksService;
  isCmdPressed?: boolean;
}) {
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>();
  useEffect(() => {
    if (containerElement == null) {
      return;
    }

    function handleResize() {
      if (containerElement == null) {
        return;
      }

      const { width, paddingLeft, paddingRight } =
        getComputedStyle(containerElement);
      setContainerWidth(
        parseFloat(width) - parseFloat(paddingLeft) - parseFloat(paddingRight),
      );
    }

    globalResizeObserver.observe(containerElement, handleResize);

    return () => globalResizeObserver.unobserve(containerElement, handleResize);
  }, [containerElement]);

  const sections = React.useMemo(
    () => session.project.homePage.sections.slice(0, sectionLimit),
    [sectionLimit, session.project.homePage.sections],
  );

  const schemes = computed(() =>
    generateSchemes({
      colorVariables: concept.colorVariables,
      chromatics: concept.chromatics,
      neutral: concept.neutral,
      cardStyle: globalCardStyleOverrideStore.get() ?? concept.cardStyle,
    }),
  ).get();

  // Some sections can't be schemed, these are denoted by a special scheme.
  // It's easier to do this here and then references it in the next step as
  // navbars need to look ahead to the next section to determine their scheme.
  const processedSections = extractSectionMetadata(library, {
    sections,
    project: session.project,
    getScheme: (elementId) =>
      schemes.find(
        (scheme) =>
          getShadeKey(scheme) ===
          concept.state.schemeOverrideByElementId.get(elementId),
      ) ?? schemes[concept.state.theme === 'light' ? 0 : 6],
    concept,
    uiStyle: globalUiStyleOverrideStore.get() ?? concept.uiStyle,
  });

  const overrides = {
    headingFontFamilyId: globalFontOverrideStore.getFontOverride('heading'),
    bodyFontFamilyId: globalFontOverrideStore.getFontOverride('body'),
    typeSettings: globalTypeSettingsOverrideStore.typeSettingsOverrides,
    uiStyle: globalUiStyleOverrideStore.get() ?? undefined,
    cardStyle: globalCardStyleOverrideStore.get() ?? undefined,
  };

  useLoadConceptFonts({ concept, blocksService, overrides });

  return (
    <StyledElementsContext
      concept={concept}
      library={library}
      overrides={overrides}
    >
      <div
        ref={setContainerElement}
        className={classNames(
          styles.container,
          !editing && styles.readonly,
          breakpoint === 'mobile' && styles.mobile,
        )}
        style={
          {
            ...(concept.isShuffling ? { '--body-opacity': '0.5' } : {}),
          } as CSSProperties
        }
      >
        <Rows spacing={0}>
          {processedSections.map(({ sref, scheme, navbarType }, index) => {
            return (
              <StyledSection
                navigation={navigation}
                analytics={analytics}
                sref={sref}
                scheme={scheme}
                session={session}
                selection={selection}
                selectionActions={selectionActions}
                library={library}
                breakpoint={breakpoint}
                width={width}
                concept={concept}
                key={index}
                containerWidth={containerWidth}
                isCmdPressed={isCmdPressed}
                navbarType={navbarType}
              />
            );
          })}
        </Rows>
      </div>
    </StyledElementsContext>
  );
});

const StyledSection = observer(function _StyledSection({
  navigation,
  analytics,
  sref,
  session,
  scheme,
  selection,
  selectionActions,
  library,
  breakpoint,
  width,
  concept,
  containerWidth,
  isCmdPressed,
  navbarType,
}: {
  navigation?: SidebarNavigation;
  analytics: AnalyticsService;
  sref: SectionRef;
  session: ProjectSession;
  scheme: CompiledScheme;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  library: LibraryCache;
  breakpoint: Breakpoint;
  width: number | null;
  concept: ConceptAPI;
  containerWidth?: number;
  isCmdPressed: boolean;
  navbarType?: 'navbar' | 'floating-navbar';
}) {
  const bp = useMemo(
    () =>
      width !== null
        ? { ...BREAKPOINT_SIZE[breakpoint], width }
        : BREAKPOINT_SIZE[breakpoint],
    [breakpoint, width],
  );
  const breakpoints = React.useMemo(
    () => getBreakpointForViewport(bp.width),
    [bp],
  );
  const section = unrefSection(sref, session.project);

  const [clickAnimationState, setClickAnimationState] = useState<{
    counter: number;
    x: number;
    y: number;
  } | null>(null);

  const openBrandPanel = useStableCallback((e: MouseEvent) => {
    e.preventDefault();
    navigation?.goto({ path: 'brand' });
  });

  const handleEditImages = useStableCallback((e: MouseEvent) => {
    e.preventDefault();
    if (section) {
      navigation?.goto({ path: 'imagery', section });
    }
  });

  const handleClick = useStableCallback((e: MouseEvent) => {
    if (e.defaultPrevented) {
      return;
    }

    if (navbarType === 'navbar') {
      return openBrandPanel(e);
    }

    if (
      // If the scheme can't be controlled, then we're shuffling images.
      !canControlScheme ||
      // Shift-click to shuffle images just for this section.
      (e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey)
    ) {
      analytics.track({
        event: 'project:image_shuffle',
        scope: 'section',
        section_id: elementId,
        section_slug: elementSlug,
      });
      populateImagesForSection(sref, session, concept, library, {
        overwrite: true,
      }).then((results) => {
        for (const [key, placeholderId] of Object.entries(results)) {
          concept.addPlaceholderImage(key, placeholderId);
        }
      });
    } else {
      analytics.track({
        event: 'project:scheme_shuffle',
        scope: 'section',
        section_id: elementId,
        section_slug: elementSlug,
        scheme_direction: e.metaKey ? 'shade' : 'palette',
      });

      concept.setSchemeForSection(
        elementId,
        getShadeKey(
          e.metaKey
            ? // Command-click to shuffle the shade for this section.
              cycleShadeInSequence(scheme, e.shiftKey ? 'backward' : 'forward')
            : // Click to shuffle the scheme for this section.
              cyclePaletteInSequence(
                scheme,
                concept.chromatics.length,
                concept.state.theme,
              ),
        ),
      );
    }

    setClickAnimationState((prev) => ({
      counter: (prev?.counter ?? 0) + 1,
      x: e.clientX,
      y: e.clientY,
    }));
  });

  const [hasImages, setHasImages] = useState(false);

  useEffect(() => {
    if (section?.element.current == null) return;
    findOverridableImagesInElement(
      section.element.current.state,
      library,
      session.undo,
    ).then((images) => {
      setHasImages(images.length > 0);
    });
  }, [library, section?.element, session.undo]);

  // Hacky way to re-render the "navbar" when the logo changes.
  // Why?
  // * The rendering of the logo is not 100% done by React (`injectLogo` in `image_styles.tsx`
  // adds imperatively the <divs> responsible for showing the logos, while `hackedSchemeVariables`
  // is responsible for updating the CSS variables used to show/hide the appropriate logo).
  // * Because of that we cannot collocate the re-render when the logo asset is loaded.
  // * `<StyledSection>` is the _closest_ React component to the injected html/css.
  // * We need to re-render _all_ `<StyledSections>` because a logo can be on any `<StyledSection>`.
  // * Alternatively, we could inspect all the element tree and find the <imag> point to a logo
  //   but that's a hairy process.
  const conceptsStore = getGlobalConceptsStore();
  conceptsStore.getRenderedLogo('dark')?.value?.src;
  conceptsStore.getRenderedLogo('light')?.value?.src;

  if (section?.element.current?.id == null) {
    return null;
  }

  const elementId = section.element.current.id;
  const elementSlug = section.element.current.source.slug;

  const canControlScheme = navbarType !== 'navbar' && scheme.shade !== -1;

  return (
    <>
      <div
        className={classNames(
          styles.section,
          styles[`breakpoint-${breakpoint}`],
          navbarType === 'navbar' && styles.navbarHack,
          navbarType === 'floating-navbar' && styles.floatingNavbarHack,
        )}
        key={elementId}
        onClick={handleClick}
      >
        <div className={styles.element}>
          <StyledElementView
            sref={sref.state}
            rootElement={section.element.current.state}
            library={library}
            selection={selection}
            isRootSelected={false}
            onSelect={() => {}}
            selectionActions={selectionActions}
            textEditing={undefined}
            outerWidth={containerWidth ?? bp.width * BREAKPOINT_TO_CANVAS_SCALE}
            innerWidth={bp.width}
            innerViewHeight={bp.height}
            visibilityDecorators={makeSectionVisibilityDecorators(
              section.element.current.state,
            )}
            flags={session.flags}
            breakpoints={breakpoints}
            concept={concept}
            scheme={scheme}
          />
          <div
            className={classNames(
              styles.highlight,
              globalConceptSectionHoverRegistry.has(
                section.element.current.id,
              ) && styles.highlighted,
            )}
          >
            <div className={styles.border} />
          </div>
          <div className={styles.overlay}>
            <div className={styles.border} />
            <div className={styles.controlsContainer}>
              <Controls>
                {[
                  !isCmdPressed &&
                    (navbarType === 'navbar' ||
                      navbarType === 'floating-navbar') && (
                      <Button
                        icon={<IconLogo />}
                        onClick={openBrandPanel}
                        variant="secondary"
                        size="small"
                        tooltip="Edit logo"
                      />
                    ),

                  (canControlScheme || hasImages) && (
                    <Inline spacing={8}>
                      {canControlScheme && (
                        <ColorControls
                          analytics={analytics}
                          elementId={elementId}
                          elementSlug={elementSlug}
                          scheme={scheme}
                          isCmdPressed={isCmdPressed}
                          concept={concept}
                          navigation={navigation}
                          session={session}
                        />
                      )}
                      {hasImages && (
                        <Button
                          icon={<IconPng />}
                          onClick={handleEditImages}
                          variant="secondary"
                          size="small"
                          tooltip="Edit images"
                        />
                      )}
                    </Inline>
                  ),
                ].filter(Boolean)}
              </Controls>
            </div>
          </div>
        </div>
      </div>
      {clickAnimationState && (
        <ClickAnimation
          x={clickAnimationState.x}
          y={clickAnimationState.y}
          key={`${clickAnimationState.counter}`}
        />
      )}
    </>
  );
});

const ClickAnimation = ({ x, y }: { x: number; y: number }) => {
  return (
    <Layer>
      <div className={styles.clickAnimation} style={{ top: y, left: x }}>
        <LottiePlayer
          animationData={clickAnimationLottie}
          loop={false}
          hideOnComplete
        />
      </div>
    </Layer>
  );
};

const shadePostfixes: ReadonlyArray<string> = [
  ' White',
  ' Lightest',
  ' Lighter',
  ' Light',
  '',
  ' Dark',
  ' Darker',
  ' Darkest',
];

const ColorControls = observer(function _ColorControls({
  analytics,
  elementId,
  elementSlug,
  scheme,
  isCmdPressed,
  concept,
  navigation,
  session,
}: {
  analytics: AnalyticsService;
  elementId: string;
  elementSlug: string;
  scheme: CompiledScheme;
  isCmdPressed: boolean;
  concept: ConceptAPI;
  navigation?: SidebarNavigation;
  session: ProjectSession;
}) {
  const colorPips = useMemo<
    { key: ShadeKey; leftHex: string; rightHex: string; name: string }[]
  >(() => {
    if (isCmdPressed) {
      if (scheme.palette === 'neutral') {
        return concept.neutral.shades.map((shadeHex, index) => {
          const key = getShadeKey({ ...scheme, shade: index });
          return {
            key,
            leftHex: shadeHex,
            rightHex: shadeHex,
            name: `Neutral${shadePostfixes[index]}`,
          };
        });
      }

      const chromatic = concept.chromatics[scheme.chromatic - 1];
      return chromatic.shades.map((shadeHex, index) => {
        const shade = index + 1;
        const key = getShadeKey({ ...scheme, shade });
        return {
          key,
          leftHex: shadeHex,
          rightHex: shadeHex,
          name: `${chromatic.name}${shadePostfixes[shade]}`,
        };
      });
    }

    if (scheme.palette === 'neutral') {
      const key = getShadeKey(scheme);
      const shadeHex = concept.neutral.shades[scheme.shade];
      const chromaticShade = neutralToChromaticShade(
        scheme.shade,
        concept.state.theme,
      );
      return [
        {
          key,
          leftHex: '#000000',
          rightHex: shadeHex,
          name: `Neutral${shadePostfixes[scheme.shade]}`,
        },
        ...concept.chromatics.map((chromatic, index) => {
          const leftHex = chromatic.hex;
          const rightHex = chromatic.shades[chromaticShade - 1];
          return {
            key: getShadeKey({
              palette: 'chromatic',
              chromatic: index + 1,
              shade: chromaticShade,
            }),
            leftHex,
            rightHex,
            name: `${chromatic.name}${shadePostfixes[chromaticShade]}`,
          };
        }),
      ];
    }

    const neutralShade = chromaticToNeutralShade(
      scheme.shade,
      concept.state.theme,
    );

    const shadeHex = concept.neutral.shades[neutralShade];
    return [
      {
        key: getShadeKey({
          palette: 'neutral',
          shade: neutralShade,
        }),
        leftHex: '#000000',
        rightHex: shadeHex,
        name: `Neutral${shadePostfixes[neutralShade]}`,
      },
      ...concept.chromatics.map((chromatic, index) => {
        // Special case for the current chromatic scheme.
        if (index === scheme.chromatic - 1) {
          return {
            key: getShadeKey(scheme),
            leftHex: chromatic.hex,
            rightHex: chromatic.shades[scheme.shade - 1],
            name: `${chromatic.name}${shadePostfixes[scheme.shade]}`,
          };
        }

        return {
          key: getShadeKey({
            palette: 'chromatic',
            chromatic: index + 1,
            shade: scheme.shade,
          }),
          leftHex: chromatic.hex,
          rightHex: chromatic.shades[scheme.shade - 1],
          name: `${chromatic.name}${shadePostfixes[scheme.shade]}`,
        };
      }),
    ];
  }, [concept, scheme, isCmdPressed]);

  const handleClick = useStableCallback((shadeKey: ShadeKey) => {
    const shadeDescriptor = parseShadeKey(shadeKey);
    if (shadeDescriptor == null) {
      return;
    }

    concept.setSchemeForSection(elementId, shadeKey);

    analytics.track({
      event: 'project:section_scheme_update',
      section_id: elementId,
      section_slug: elementSlug,
      palette: shadeDescriptor.palette,
      chromatic: shadeDescriptor.chromatic,
      shade: shadeDescriptor.shade,
    });
  });

  return (
    <div
      className={classNames(styles.colors, isCmdPressed && styles.cmd)}
      onClick={(e) => e.preventDefault()}
    >
      {isCmdPressed && (
        <Button
          icon={<IconEdit />}
          size="small"
          variant="secondary"
          onClick={() =>
            navigation?.goto({
              path: `colors/${
                scheme.palette === 'neutral'
                  ? 'neutral'
                  : `chromatic${scheme.chromatic}`
              }` as ColorsRoute['path'],
            })
          }
          tooltip={`Edit ${scheme.palette === 'neutral' ? 'neutral' : 'color'}`}
        />
      )}
      <Inline spacing={4} yalign="center">
        {colorPips.map(({ key, leftHex, rightHex, name }) => (
          <ShadeButton
            key={key}
            shadeKey={key}
            shadeName={name}
            leftHex={leftHex}
            rightHex={rightHex}
            active={getShadeKey(scheme) === key}
            onClick={handleClick}
            chromaticMode={!isCmdPressed && leftHex === rightHex}
          />
        ))}
      </Inline>
      <BespokeVerticalDivider />
      {!isCmdPressed ? (
        <div style={{ color: COLOR_TOKENS.colorGrey }}>
          <Shortcut label="View Shades" shortcut="Hold ⌘" size="tiny" />
        </div>
      ) : (
        <ShadeName concept={concept} scheme={scheme} />
      )}
    </div>
  );
});

export const ShadeButton = observer(function _ShadeButton({
  shadeKey,
  shadeName,
  leftHex,
  rightHex,
  active,
  onClick,
  chromaticMode,
}: {
  shadeKey: ShadeKey;
  shadeName: string;
  leftHex: string;
  rightHex: string;
  active: boolean;
  onClick: (shadeKey: ShadeKey) => void;
  chromaticMode: boolean;
}) {
  const handleClick = useStableCallback((e: MouseEvent) => {
    e.preventDefault();
    onClick(shadeKey);
  });

  return (
    <ColorPip
      name={shadeName}
      active={active}
      leftHex={leftHex}
      rightHex={rightHex}
      onClick={handleClick}
      chromaticMode={chromaticMode}
    />
  );
});

function ShadeName({
  concept,
  scheme,
}: {
  concept: ConceptAPI;
  scheme: CompiledScheme;
}) {
  const shadePostfixes = [
    'White',
    'Lightest',
    'Lighter',
    'Light',
    null,
    'Dark',
    'Darker',
    'Darkest',
  ];

  const colorName =
    scheme.palette === 'neutral'
      ? 'Neutral'
      : concept.chromatics[scheme.chromatic - 1].name;

  const shadePostfix = shadePostfixes[scheme.shade];
  return (
    <TextTinyLight color="grey">
      <span>{colorName}</span>{' '}
      <span style={{ display: 'inline-block', width: '47px' }}>
        {/* Hard coded to be the max possible width */}
        {shadePostfix}
      </span>
    </TextTinyLight>
  );
}

const BespokeVerticalDivider = () => (
  <div
    style={{
      width: '1px',
      height: '20px',
      background: COLOR_TOKENS.colorLightGrey3,
    }}
  />
);

const Controls = ({ children }: { children: React.ReactNode[] }) => {
  if (children.length === 0) return null;

  return (
    <div className={styles.controls}>
      {children.map((child, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <BespokeVerticalDivider />}
          {child}
        </React.Fragment>
      ))}
    </div>
  );
};
